import {
  buildCameraAudioUrl,
  SendAudioProduct,
} from '@hakimo-ui/hakimo/data-access';
import { HttpClient } from '@hakimo-ui/hakimo/types';
import { toast, useHttpClient } from '@hakimo-ui/hakimo/util';
import { useMutation, useQueryClient } from 'react-query';

interface SendTalkdownOptions {
  locationAlarmId: string;
  escalationId?: string;
  product?: SendAudioProduct;
  onSuccess?: (cameraIds: string[]) => void;
}

function send(
  httpClient: HttpClient,
  cameraId: string,
  audio: FormData,
  options: SendTalkdownOptions
) {
  const { locationAlarmId, escalationId, product } = options;
  return httpClient<null>(
    new Request(
      buildCameraAudioUrl(cameraId, {
        locAlarmId: locationAlarmId,
        escalationId,
        product,
      }),
      { method: 'POST', body: audio }
    )
  );
}

export function useSendTalkdown(options: SendTalkdownOptions) {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation<string[], Error, { cameraIds: string[]; audio: FormData }>(
    async ({ cameraIds, audio }) => {
      const results = await Promise.allSettled(
        cameraIds.map((cameraId) => send(httpClient, cameraId, audio, options))
      );
      return cameraIds.filter((_, idx) => results[idx].status === 'fulfilled');
    },
    {
      onSuccess: (sentTo, { cameraIds }) => {
        queryClient.invalidateQueries(['location_alarm_updates']);
        queryClient.invalidateQueries([
          'alarm_updates',
          '',
          options.locationAlarmId,
        ]);

        if (sentTo.length === 0) {
          toast(
            'Talkdown failed — audio was not sent to any camera. Please retry.',
            { type: 'error' }
          );
          return;
        }
        if (sentTo.length < cameraIds.length) {
          toast(
            `Audio sent to ${sentTo.length} of ${cameraIds.length} cameras.`,
            { type: 'error' }
          );
        } else {
          toast(
            sentTo.length === 1
              ? 'Audio sent to camera successfully'
              : `Audio sent to ${sentTo.length} cameras successfully`
          );
        }
        options.onSuccess?.(sentTo);
      },
    }
  );
}

export default useSendTalkdown;
